import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useWikiPage } from '@/hooks/useWikiPage';
import { SeoHead } from '@/components/seo/SeoHead';
import { JsonLd } from '@/components/seo/JsonLd';
import { slugify } from '@/utils/slug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

// Breadcrumbs removed on public page; the left TOC reflects content structure

const Toc: React.FC<{ sections: ReturnType<typeof useWikiPage>['data'] extends infer T ? T extends { sections: infer S } ? S : any : any }>
  = ({ sections }) => {
  const flatten = (nodes: any[], depth = 2): { anchor: string; title: string; depth: number }[] => {
    const out: { anchor: string; title: string; depth: number }[] = [];
    for (const n of nodes || []) {
      out.push({ anchor: n.anchor_id || n.slug, title: n.title, depth });
      if (n.children?.length) out.push(...flatten(n.children, Math.min(depth + 1, 6)));
    }
    return out;
  };
  const items = flatten(sections as any[]);
  if (items.length === 0) return null;
  return (
    <aside className="border rounded-md p-4 bg-muted/30">
      <h2 className="text-sm font-semibold mb-2">Table of contents</h2>
      <ul className="space-y-1">
        {items.map((it, i) => {
          const indentClasses = ['ml-0','ml-2','ml-4','ml-6','ml-8'];
          const idx = Math.max(0, Math.min(indentClasses.length - 1, (it.depth - 2)));
          const indentClass = indentClasses[idx];
          return (
            <li key={i} className={indentClass}>
            <a href={`#${it.anchor}`} className="text-sm hover:underline">{it.title}</a>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

const SectionTree: React.FC<{ nodes: any[]; level?: number }> = ({ nodes, level = 2 }) => {
  const Heading: any = `h${Math.min(level, 6)}`;
  return (
    <div className="space-y-6">
      {nodes?.map((n) => (
        <section key={n.id} id={n.anchor_id || n.slug}>
          <Heading className="scroll-mt-20">{n.title}</Heading>
          <div className="prose prose-sm md:prose-base whitespace-pre-wrap mt-2">{n.content_md}</div>
          {n.children?.length ? <SectionTree nodes={n.children} level={level + 1} /> : null}
        </section>
      ))}
    </div>
  );
};


const PublicWikiPage: React.FC = () => {
  const { positId, slug } = useParams<{ positId: string; slug?: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useWikiPage(positId);
  const [searchOpen, setSearchOpen] = React.useState(false);
  // Public page is read-only; editing happens in the private editor page

  const title = data?.posit.title || 'Profile';
  const isPublic = Boolean(data?.seo.is_public);
  const isMonetized = Boolean(data?.posit?.is_monetized);
  const desiredSlug = data ? (data.posit.title || 'profile') : 'profile';
  const slugified = React.useMemo(() => slugify(desiredSlug), [desiredSlug]);
  const canonical = data ? `${window.location.origin}/d/${data.posit.id}/${slugified}` : undefined;
  const description = data?.lead?.slice(0, 160);

  const jsonLd = data ? {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: canonical,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Profiles', item: `${window.location.origin}/public-posits` },
        { '@type': 'ListItem', position: 3, name: title, item: canonical }
      ]
    }
  } : null;

  // If route slug mismatches, redirect to canonical slug form for consistency
  React.useEffect(() => {
    if (!data) return;
    if (slug !== slugified) {
      navigate(`/d/${data.posit.id}/${slugified}`, { replace: true });
    }
  }, [data, slug, slugified, navigate]);

  // Build content-aware search index for palette
  const searchItems = React.useMemo(() => {
    const toPlain = (md: string | undefined): string => {
      const text = (md || '');
      return text
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]*`/g, ' ')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/^#+\s*/gm, '')
        .replace(/[>*_~#`]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    const items: { anchor: string; title: string; text: string }[] = [];
    const visit = (nodes: any[]) => {
      for (const n of nodes || []) {
        const anchor = n.anchor_id || n.slug;
        items.push({
          anchor,
          title: n.title || anchor,
          text: toPlain(n.content_md || '').slice(0, 400)
        });
        if (n.children?.length) visit(n.children);
      }
    };
    visit(data?.sections || []);
    return items;
  }, [data?.sections]);

  // Open palette with Cmd/Ctrl+K
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Build FAQ and HowTo JSON-LD from sections
  const howToSchemas = React.useMemo(() => {
    if (!data?.sections) return [] as any[];
    const items: any[] = [];
    const visit = (nodes: any[]) => {
      for (const n of nodes) {
        const t = (n.title || '').toLowerCase();
        const isHowTo = t.includes('how‑to') || t.includes('how-tos') || t.includes('how to');
        if (isHowTo) {
          const lines = (n.content_md || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
          const steps = lines
            .filter(l => /^(-|\d+\.|•)/.test(l))
            .map(l => l.replace(/^(-|\d+\.|•)\s*/, ''))
            .slice(0, 10)
            .map(text => ({ '@type': 'HowToStep', text }));
          if (steps.length >= 2) {
            items.push({ '@context': 'https://schema.org', '@type': 'HowTo', name: n.title, step: steps });
          }
        }
        if (n.children?.length) visit(n.children);
      }
    };
    visit(data.sections);
    return items;
  }, [data]);

  const faqSchema = React.useMemo(() => {
    if (!data?.sections) return null as any;
    const items: any[] = [];
    const visit = (nodes: any[]) => {
      for (const n of nodes) {
        const t = (n.title || '').toLowerCase();
        const isFaq = t.includes('faq') || t.includes('questions');
        if (isFaq) {
          const pairs: { q: string; a: string }[] = [];
          const lines = (n.content_md || '').split(/\r?\n/);
          let q: string | null = null;
          let a: string[] = [];
          for (const line of lines) {
            if (line.trim().toLowerCase().startsWith('q:')) {
              if (q) pairs.push({ q, a: a.join(' ').trim() });
              q = line.replace(/^q:\s*/i, '').trim();
              a = [];
            } else if (line.trim().toLowerCase().startsWith('a:')) {
              a.push(line.replace(/^a:\s*/i, '').trim());
            } else if (a.length > 0) {
              a.push(line.trim());
            }
          }
          if (q) pairs.push({ q, a: a.join(' ').trim() });
          const mainEntity = pairs.slice(0, 10).map(p => ({ '@type': 'Question', name: p.q, acceptedAnswer: { '@type': 'Answer', text: p.a } }));
          if (mainEntity.length > 0) {
            return { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity };
          }
        }
        if (n.children?.length) {
          const child = visit(n.children);
          if (child) return child;
        }
      }
      return null;
    };
    return visit(data.sections);
  }, [data]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="px-4 py-12 text-muted-foreground">Loading…</div>
      </DashboardLayout>
    );
  }
  if (isError || !data) {
    return (
      <DashboardLayout>
        <div className="px-4 py-12 text-red-600">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <SeoHead
          title={`Posit: ${title}`}
          description={description || (isPublic && isMonetized ? `${title} — premium Posit. Chat to explore capabilities.` : undefined)}
          canonicalUrl={canonical}
          indexable={isPublic}
          robotsMaxSnippet={isPublic && isMonetized ? 120 : undefined}
          ogType="article"
        />
        {jsonLd ? <JsonLd data={jsonLd} /> : null}
        {faqSchema ? <JsonLd data={faqSchema} /> : null}
        {howToSchemas.map((s, i) => (<JsonLd key={i} data={s} />))}

        {/* No breadcrumb trail; use the left-hand Table of Contents for navigation */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">{title}</h1>
            {data.lead && (
              <p className="text-muted-foreground">{data.lead}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-56 hidden md:block">
              <Input
                placeholder="Search sections… (⌘K)"
                onFocus={() => setSearchOpen(true)}
                readOnly
              />
            </div>
            <Link to={`/chat/${data.posit.id}`} className="inline-flex items-center justify-center h-9 w-9 rounded-md border hover:bg-accent">
              <MessageSquare className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {isMonetized && isPublic && !data.permissions?.is_owner ? (
          <div className="mt-6 border rounded-md p-6 bg-muted/30 text-center">
            <div className="font-medium mb-2">This Posit’s detailed content is for subscribers.</div>
            <div className="text-sm text-muted-foreground mb-4">Chat with this Posit to explore capabilities or subscribe from the creator’s page.</div>
            <Link to={`/chat/${data.posit.id}`}><Button size="sm">Open Chat</Button></Link>
          </div>
        ) : (!data.lead && (!data.sections || data.sections.length === 0)) ? (
          <div className="mt-6 border rounded-md p-6 bg-muted/30">
            <div className="text-sm text-muted-foreground">No public content yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1 order-last md:order-first">
              <Toc sections={data.sections} />
            </div>
            <div className="md:col-span-3">
              <SectionTree nodes={data.sections} />
            </div>
          </div>
        )}
      </div>
      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput placeholder="Search sections…" />
        <CommandList>
          <CommandEmpty>No sections found.</CommandEmpty>
          <CommandGroup heading="Sections">
            {searchItems.map((s) => (
              <CommandItem key={s.anchor} value={`${s.title} ${s.text}`} onSelect={() => {
                setSearchOpen(false);
                const el = document.getElementById(s.anchor);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Also update hash for shareability
                if (typeof window !== 'undefined') window.history.replaceState(null, '', `#${s.anchor}`);
              }}>
                <div>
                  <div className="text-sm font-medium">{s.title}</div>
                  {s.text && (
                    <div className="text-xs text-muted-foreground line-clamp-2">{s.text}</div>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </DashboardLayout>
  );
};

export default PublicWikiPage;


